import type { Appearance } from '@/hooks/use-appearance';
import { type ImgHTMLAttributes, useEffect, useState } from 'react';

type ThemedImgProps = ImgHTMLAttributes<HTMLImageElement> & {
    src: string;
    darkSrc?: string;
    initialDark?: boolean;
    appearance?: Appearance;
};

export function ThemedImg({ src, darkSrc, initialDark, appearance, ...props }: ThemedImgProps) {
    // Keep the server and first client render identical. The effect below
    // reconciles the actual document theme immediately after hydration.
    const [isDark, setIsDark] = useState(initialDark ?? false);
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        const root = document.documentElement;
        const sync = () => setIsDark(root.classList.contains('dark'));

        sync();
        setMounted(true);
        const observer = new MutationObserver(sync);
        observer.observe(root, { attributes: true, attributeFilter: ['class'] });

        return () => observer.disconnect();
    }, []);

    if (appearance === 'system' && darkSrc && !mounted) {
        return (
            <picture className="contents">
                <source media="(prefers-color-scheme: dark)" srcSet={darkSrc} />
                <img src={src} {...props} />
            </picture>
        );
    }

    return <img src={darkSrc && isDark ? darkSrc : src} {...props} />;
}
